import React from "react";
import Layout from "@/components/Layout";
import { FadeUp } from "@/components/Motion";

const UPDATED = "January 1, 2026";

const Terms = () => (
  <Legal
    title="Terms of Use"
    intro="These terms govern access to and use of the Travel + Leisure Supplier Engagement Platform, produced by Display America."
    sections={[
      { h: "1. Acceptance", p: "By accessing this platform you agree to these terms and to the Privacy Policy. If you do not agree, do not use the platform." },
      { h: "2. Eligible users", p: "This platform is for registered suppliers, invited attendees, and authorized Travel + Leisure and Display America personnel. Access credentials are personal and may not be shared." },
      { h: "3. Acceptable use", p: "You will not (a) attempt to circumvent authentication or rate limits, (b) scrape or bulk-download the directory, (c) impersonate another user, or (d) upload content you do not have the right to distribute." },
      { h: "4. Content ownership", p: "You retain ownership of the company information, logos, and artwork you upload. You grant Travel + Leisure and Display America a limited license to display that content within the platform for the Strategic Supplier Summit and successor events." },
      { h: "5. Payment", p: "The $2,500 Strategic Supplier Package is processed by Stripe. Refunds are governed by the exhibitor agreement signed at registration." },
      { h: "6. Confidentiality", p: "Procurement conversations, sourcing priorities, and executive briefings visible on this platform may be confidential. You will not share confidential materials outside your organization without written consent." },
      { h: "7. Warranty & liability", p: "The platform is provided on an \"as available\" basis. Display America and Travel + Leisure disclaim implied warranties. Liability is limited to the fees paid for the current event." },
      { h: "8. Governing law", p: "These terms are governed by the laws of the State of Florida, United States." },
      { h: "9. Contact", p: "Legal notices: info@displayamerica.com." },
    ]}
  />
);

const Privacy = () => (
  <Legal
    title="Privacy Policy"
    intro="This policy explains what we collect on the platform, how we use it, and your choices."
    sections={[
      { h: "What we collect", p: "Business contact details you submit (name, company, email, phone), files you upload (logos, artwork, capability statements), and platform activity (page views, QR scans, meeting requests). We do not collect payment card data — that is processed directly by Stripe." },
      { h: "How we use it", p: "To operate the platform, facilitate procurement engagement, personalize matchmaking, deliver executive communications, and generate aggregate event reporting for Travel + Leisure and Display America." },
      { h: "Third parties", p: "SendGrid delivers transactional email. Stripe processes payments. Both are contractually bound to protect your data. No personal data is sold or shared for advertising." },
      { h: "Retention", p: "Live event data is retained for 18 months after the event. Aggregate, de-identified analytics may be retained indefinitely for benchmarking future events." },
      { h: "Your rights", p: "You may request access, correction, or deletion of your personal data by emailing info@displayamerica.com. We respond within 30 days." },
      { h: "Security", p: "The platform uses TLS in transit, encrypted database backups, role-based access control, structured audit logging, and rate limiting on authentication and public endpoints." },
      { h: "Contact", p: "Privacy questions: info@displayamerica.com." },
    ]}
  />
);

const DataRetention = () => (
  <Legal
    title="Data Retention"
    intro="The Travel + Leisure Supplier Engagement Platform retains data long enough to support the current event, post-event reporting, and reasonable business follow-up — and no longer."
    sections={[
      { h: "Registration & profile", p: "Retained for 18 months after the event date. Suppliers may request earlier deletion." },
      { h: "Payments (Stripe references)", p: "Retained for 7 years to satisfy tax and audit requirements. Only Stripe payment references are stored on the platform — card data lives with Stripe." },
      { h: "Uploaded artwork", p: "Retained for 24 months, after which un-referenced files are archived to cold storage and deleted from the live database." },
      { h: "Communications log & delivery events", p: "Retained for 24 months for audit and CAN-SPAM compliance." },
      { h: "Audit log", p: "Retained for 24 months. Records administrator actions (approvals, exports, config changes) for security and compliance review." },
      { h: "Aggregate analytics", p: "De-identified event totals (attendee counts, meeting volume, engagement percentages) may be retained indefinitely for year-over-year benchmarking." },
      { h: "Requests", p: "Deletion or export requests: info@displayamerica.com." },
    ]}
  />
);

function Legal({ title, intro, sections }) {
  return (
    <Layout>
      <section className="border-b border-[hsl(var(--line))] bg-[#0A0A0A] text-white">
        <div className="container-summit py-16 md:py-20">
          <FadeUp className="overline text-zinc-400 mb-4"><span className="accent-rule" />Legal</FadeUp>
          <FadeUp><h1 className="serif text-4xl sm:text-6xl leading-[1.05]">{title}</h1></FadeUp>
          <FadeUp delay={0.05}><p className="mt-6 text-white/75 max-w-2xl leading-relaxed">{intro}</p></FadeUp>
          <FadeUp delay={0.08}><div className="mt-6 text-[11px] uppercase tracking-[0.22em] text-white/50">Last updated: {UPDATED}</div></FadeUp>
        </div>
      </section>
      <section className="container-summit py-12 md:py-16 max-w-3xl">
        <div className="space-y-10">
          {sections.map((s) => (
            <FadeUp key={s.h}>
              <h2 className="serif text-xl md:text-2xl">{s.h}</h2>
              <p className="mt-3 text-zinc-700 leading-relaxed">{s.p}</p>
            </FadeUp>
          ))}
        </div>
      </section>
    </Layout>
  );
}

export { Terms, Privacy, DataRetention };
